/**
 * The local clone of a repository linked BY URL (Run section, 26.10.2026):
 * "Choose local clone…" checks a folder the user picked, "Clone here…" clones
 * into a new one. Either way the folder is remembered per device and per
 * account in `services/git/codeRepoClones.js` — the same store the commit ↔
 * task links use ("point to your local clone once per device"), so one pick
 * serves both. Never in the synced repo record: a folder names one person's
 * disk, and a team repository has no folder at all.
 *
 * Rust: `code_repo_origin` / `code_repo_clone` (src-tauri/src/code_repo_clone.rs).
 * The Tauri API is imported lazily (tests/tauriImportsAreLazy).
 */
import { hostOf } from '../../../graph/repoRefs';
import { ownerRepoOf } from '../../../services/git/providers/remote';
import { sameRepository, setClonePath } from '../../../services/git/codeRepoClones';

async function invoke(cmd, args) {
  const core = await import('@tauri-apps/api/core');
  return core.invoke(cmd, args);
}

/** Folder picker; '' when cancelled. */
export async function pickFolder(defaultPath) {
  const { open } = await import('@tauri-apps/plugin-dialog');
  const picked = await open({ directory: true, multiple: false, ...(defaultPath ? { defaultPath } : {}) });
  return typeof picked === 'string' ? picked : '';
}

/** → { root, remoteUrl, headBranch } of a folder (Rust error codes code_repo_*). */
export const readCloneOrigin = (repoPath) => invoke('code_repo_origin', { repoPath });

/**
 * Is `path` a clone of `repo`? → { status: 'ok', root, origin }
 * | { status: 'mismatch', root, linked, origin } (origin '' = the folder has no origin)
 * | throws the Rust error (not a git repository, missing, refused).
 */
export async function checkCloneFolder(repo, path) {
  const res = (await readCloneOrigin(path)) || {};
  const root = res.root || path;
  const origin = res.remoteUrl || '';
  if (repo?.remote && origin && sameRepository(repo.remote, origin)) return { status: 'ok', root, origin, headBranch: res.headBranch || null };
  return { status: 'mismatch', root, linked: repo?.remote || '', origin, headBranch: res.headBranch || null };
}

/** Remember `root` as this device's clone of `repo`. Throws when storage refuses. */
export function linkClone(repo, root) {
  if (!setClonePath(repo.id, root)) throw new Error('storage_refused');
}

/** Forget this device's clone (the folder itself is left alone). */
export function unlinkClone(repo) {
  setClonePath(repo.id, '');
}

/** The folder name a clone gets by default: the repository's name. */
export function defaultFolderName(repo) {
  const fromUrl = repo?.remote ? ownerRepoOf(repo.remote).repo : '';
  const raw = fromUrl && fromUrl !== 'repository' ? fromUrl : repo?.name || 'repository';
  const safe = Array.from(String(raw), (ch) => (ch.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(ch) ? '-' : ch)).join('');
  return safe.replace(/^\.+/, '').slice(0, 100) || 'repository';
}

/** `parent` + `name` with the parent's own separator. */
export function joinPath(parent, name) {
  const p = String(parent || '').replace(/[\\/]+$/, '');
  const sep = p.includes('\\') && !p.includes('/') ? '\\' : '/';
  return `${p}${sep}${name}`;
}

/**
 * The sign-in to preselect for `repo` among the Git page's clone sources
 * (CloneSourceFields.cloneSources): an account on the repo's host, else
 * GitHub for github.com, else "Other git server" (Vault).
 */
export function defaultSourceFor(repo, sources = []) {
  const host = hostOf(repo?.remote || '');
  if (host === 'github.com') return sources.find((s) => s.provider === 'github') || null;
  const account = sources.find((s) => s.server && hostOf(s.server) === host);
  if (account) return account;
  if (host === 'gitlab.com') {
    const gl = sources.find((s) => s.provider === 'gitlab' && !s.server);
    if (gl) return gl;
  }
  return sources.find((s) => s.provider === 'remote') || null;
}

/**
 * Clone `url` into `dest` with the chosen sign-in, the Git page's way
 * (workspaceGitSync.cloneRepo): a provider token from the webview, or a Vault
 * item that Rust reads itself. → { path, branch, branchMissing }.
 */
export async function cloneCodeRepo({ url, branch, dest, source, vaultItemId }) {
  const { getProvider } = await import('../../../services/git/providers');
  const { credArgs } = await import('../../../services/git/gitAuth');
  const provider = getProvider(source?.provider || 'github');
  const ctx = { provider: provider.id, accountId: source?.accountId || null, url, vaultItemId: vaultItemId || null };
  const token = provider.usesVault ? '' : await provider.getToken(ctx).catch(() => ''); // '' = public repository
  return invoke('code_repo_clone', { dest, url, branch: branch || null, ...credArgs(provider, token, ctx) });
}
